import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@/components/ui';

interface DepositConfirmModalProps {
  orderNo: string;
  isOpen: boolean;
  isSubmitting: boolean;
  errorMessage?: string | null;
  /** 입금 메모(선택). 비우면 빈 문자열. */
  onSubmit: (reason: string) => void;
  onCancel: () => void;
  /** QA-472 — 같은 모양으로 [취소 복원]에도 쓴다. 없으면 입금확인 문구. */
  copy?: {
    title: string;
    description: string;
    memoLabel: string;
    placeholder: string;
    submitLabel: string;
  };
}

/**
 * 무통장 입금확인 모달(M-4 ④, [ID52]). 입금대기+무통장 주문을 운영자가 수동으로
 * 입금확인 → 결제완료 전환한다. 백오피스 한국어 전용이라 OrderActionModal(i18n
 * 결합) 을 재사용하지 않고 한국어 라벨로 독립 구성한다. 입금 메모는 선택 입력이며,
 * 입력 시 confirm-deposit 의 reason 으로 전달된다(감사 로그).
 */
export const DepositConfirmModal = ({
  orderNo,
  isOpen,
  isSubmitting,
  errorMessage,
  onSubmit,
  onCancel,
  copy,
}: DepositConfirmModalProps) => {
  const [memo, setMemo] = useState('');

  useEffect(() => {
    if (!isOpen) setMemo('');
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(memo.trim());
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(o) => {
        if (!o) onCancel();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{copy?.title ?? '입금확인'}</DialogTitle>
          <DialogDescription>
            {copy?.description ??
              `주문 ${orderNo} 의 무통장 입금을 확인하고 결제완료로 전환합니다. 입금이 실제로 확인된 주문에만 적용하세요. 전환 후에는 되돌릴 수 없습니다.`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="deposit-confirm-memo"
              className="text-sm"
              style={{ color: 'var(--content-muted)' }}
            >
              {copy?.memoLabel ?? '입금 메모 (선택)'}
            </label>
            <Input
              id="deposit-confirm-memo"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              maxLength={500}
              placeholder={copy?.placeholder ?? '예: 6/6 12:30 홍길동 입금 확인'}
              data-testid="deposit-confirm-memo"
            />
          </div>

          {errorMessage ? (
            <p
              className="text-sm"
              role="alert"
              style={{ color: 'var(--status-danger-fg)' }}
            >
              {errorMessage}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              취소
            </Button>
            <Button
              type="submit"
              variant="cta"
              size="md"
              disabled={isSubmitting}
              data-testid="deposit-confirm-submit"
            >
              {isSubmitting ? '처리 중…' : (copy?.submitLabel ?? '입금확인')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
